import React, { useEffect, useRef } from 'react';
import { t } from '../i18n';
import { useApp } from '../context/AppContext';
import { fmtDateTime } from '../lib/format';

const isVideo = (url) => /\.(mp4|mov|webm|m4v)(\?|$)/i.test(url) || url.includes('/video/');

// Who wrote it, in the reader's own terms.
const roleLabel = (role) => ({
  homeowner: t('msg.roleHomeowner'),
  vendor: t('msg.roleTech'),
  admin: t('msg.roleQps'),
}[role] || '');

// The conversation attached to one job. Same component for the homeowner, the
// technician and QPS — only `currentUser` decides which side a bubble sits on.
export default function MessageThread() {
  const {
    currentUser, thread, isLoadingThread, threadFor,
    messageText, setMessageText, messageFiles, setMessageFiles,
    isSendingMessage, handleSendMessage,
  } = useApp();
  const bottom = useRef(null);

  // Keep the newest message in view as the thread loads or grows.
  useEffect(() => {
    bottom.current?.scrollIntoView({ block: 'end' });
  }, [thread.length, isLoadingThread]);

  return (
    <div>
      {threadFor && (
        <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 14 }}>
          {threadFor.serviceType}{threadFor.serviceAddress ? ` · ${threadFor.serviceAddress}` : ''}
        </div>
      )}

      <div style={{ maxHeight: '46vh', overflowY: 'auto', padding: '4px 2px', marginBottom: 14 }}>
        {isLoadingThread && <div style={{ color: 'var(--muted)', fontSize: 13, textAlign: 'center', padding: 20 }}>{t('msg.loading')}</div>}

        {!isLoadingThread && thread.length === 0 && (
          <div style={{ color: 'var(--muted)', fontSize: 13, textAlign: 'center', padding: '30px 10px', lineHeight: 1.5 }}>
            {t('msg.empty')}
          </div>
        )}

        {thread.map(m => {
          const mine = m.senderId === currentUser?.id;
          return (
            <div key={m._id} style={{ display: 'flex', justifyContent: mine ? 'flex-end' : 'flex-start', marginBottom: 10 }}>
              <div style={{
                maxWidth: '82%',
                background: mine ? 'var(--blue-light)' : 'var(--white)',
                border: '1px solid var(--border)',
                borderRadius: 12,
                padding: '8px 11px',
              }}>
                <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 3 }}>
                  {mine ? t('msg.you') : `${m.senderName} · ${roleLabel(m.senderRole)}`}
                </div>
                {m.body && <div style={{ fontSize: 14, lineHeight: 1.45, whiteSpace: 'pre-wrap' }}>{m.body}</div>}
                {m.attachments?.length > 0 && (
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: m.body ? 8 : 0 }}>
                    {m.attachments.map((src, i) => isVideo(src) ? (
                      <div
                        key={i}
                        onClick={() => window.open(src, '_blank')}
                        style={{ width: 76, height: 76, borderRadius: 8, border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', background: 'var(--bg)' }}
                      >▶</div>
                    ) : (
                      <img
                        key={i} src={src} alt="" loading="lazy"
                        onClick={() => window.open(src, '_blank')}
                        style={{ width: 76, height: 76, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--border)', cursor: 'pointer' }}
                      />
                    ))}
                  </div>
                )}
                <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 5, textAlign: 'right' }}>{fmtDateTime(m.createdAt)}</div>
              </div>
            </div>
          );
        })}
        <div ref={bottom} />
      </div>

      <div className="form-group" style={{ marginBottom: 8 }}>
        <textarea
          value={messageText}
          onChange={e => setMessageText(e.target.value)}
          placeholder={t('msg.placeholder')}
          style={{ minHeight: 70 }}
        />
      </div>

      {/* The picker is opened by a <label for>, not by button.click() on a
          display:none input. Safari refuses a scripted .click() on a file input
          it isn't rendering, which is why attaching worked nowhere on iPhone or
          on desktop Safari while the request form — whose input sits INSIDE the
          element being clicked — worked fine. A label needs no JS at all.
          The input stays in the layout but visually hidden; display:none would
          bring the same Safari problem back through the label. */}
      <div className="form-group">
        <input
          id="msg-attach"
          type="file"
          accept="image/*,video/*"
          multiple
          style={{ position: 'absolute', width: 1, height: 1, opacity: 0, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0, padding: 0, margin: -1 }}
          onChange={e => {
            // See the note in screens/Modals.js: the FileList has to be read
            // before the input is cleared, or the updater runs against an
            // empty one and the attachment silently never happens.
            const picked = Array.from(e.target.files);
            e.target.value = '';
            setMessageFiles(prev => [...prev, ...picked].slice(0, 10));
          }}
        />
        <label htmlFor="msg-attach" className="btn btn-outline btn-sm" style={{ cursor: 'pointer' }}>
          {t('msg.attach')}
        </label>
        {messageFiles.length > 0 && (
          <div className="file-chips">
            {messageFiles.map((f, i) => (
              <span key={i} className="file-chip">{f.name}
                <span style={{ marginLeft: 6, cursor: 'pointer' }} onClick={() => setMessageFiles(prev => prev.filter((_, idx) => idx !== i))}>×</span>
              </span>
            ))}
          </div>
        )}
      </div>

      <button className="btn btn-primary btn-lg" disabled={isSendingMessage} onClick={handleSendMessage}>
        {isSendingMessage ? t('req.submitting') : t('msg.send')}
      </button>
    </div>
  );
}
